<template>
  <transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="toast-notification bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
      @mouseenter="pauseTimer"
      @mouseleave="resumeTimer"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <!-- Success Icon -->
            <svg
              v-if="type === 'success'"
              class="h-6 w-6 text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <!-- Error Icon -->
            <svg
              v-else-if="type === 'error'"
              class="h-6 w-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <!-- Info Icon -->
            <svg
              v-else
              class="h-6 w-6 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium text-gray-900">{{ title }}</p>
            <p v-if="message" class="mt-1 text-sm text-gray-500">{{ message }}</p>

            <!-- Action Buttons -->
            <div v-if="showActions" class="mt-3 flex space-x-2">
              <Button
                @click="handleViewCart"
                variant="link"
                size="sm"
                data-testid="toast-view-cart-btn"
                custom-class="text-indigo-600 hover:text-indigo-500 focus:ring-indigo-500"
              >
                View Cart
              </Button>
              <Button
                @click="handleContinueShopping"
                variant="link"
                size="sm"
                data-testid="toast-continue-shopping-btn"
                custom-class="text-gray-700 hover:text-gray-500 focus:ring-indigo-500"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button
              @click="close"
              type="button"
              data-testid="toast-close-btn"
              class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              aria-label="Close notification"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Visual countdown bar -->
      <div v-if="autoClose" class="h-1 bg-gray-100">
        <div
          class="h-full rounded-br transition-none"
          :class="timerBarColorClass"
          :style="{ width: timerPercent + '%' }"
        ></div>
      </div>
    </div>
  </transition>
</template>

<script>
import Button from './Button.vue'

export default {
  name: 'ToastNotification',
  components: {
    Button
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'success',
      validator: value => ['success', 'error', 'info'].includes(value)
    },
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      default: ''
    },
    showActions: {
      type: Boolean,
      default: false
    },
    autoClose: {
      type: Boolean,
      default: true
    },
    duration: {
      type: Number,
      default: 4000
    }
  },
  data() {
    return {
      timer: null,
      tickInterval: null,
      remaining: 0,
      paused: false
    }
  },
  computed: {
    timerPercent() {
      if (!this.duration) return 0
      return Math.max(0, (this.remaining / this.duration) * 100)
    },
    timerBarColorClass() {
      if (this.type === 'error') return 'bg-red-400'
      if (this.type === 'info') return 'bg-blue-400'
      return 'bg-green-400'
    }
  },
  mounted() {
    if (this.show && this.autoClose) {
      this.startTimer()
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.autoClose) {
        this.startTimer()
      } else {
        this.clearTimer()
      }
    }
  },
  methods: {
    close() {
      this.clearTimer()
      // Defer emit to next tick to avoid _wrapper error when component is destroyed
      // during click handler execution
      this.$nextTick(() => {
        this.$emit('close')
      })
    },

    handleViewCart() {
      // Just emit the event - parent (ToastManager) will handle clearing
      // Don't call close() here to avoid race condition with Vue destroy lifecycle
      this.$emit('view-cart')
    },

    handleContinueShopping() {
      // Just emit the event - parent (ToastManager) will handle removal
      this.$emit('continue-shopping')
    },

    startTimer() {
      this.clearTimer()
      this.remaining = this.duration
      this.paused = false

      // Tick every 50ms for smooth visual countdown
      this.tickInterval = setInterval(() => {
        if (!this.paused) {
          this.remaining -= 50
          if (this.remaining <= 0) {
            this.clearTimer()
            // Emit close directly here since we're already in an async callback
            this.$emit('close')
          }
        }
      }, 50)
    },

    pauseTimer() {
      this.paused = true
    },

    resumeTimer() {
      this.paused = false
    },

    clearTimer() {
      if (this.tickInterval) {
        clearInterval(this.tickInterval)
        this.tickInterval = null
      }
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.remaining = 0
    }
  },
  beforeDestroy() {
    this.clearTimer()
  }
}
</script>

<style scoped>
.toast-notification {
  position: relative;
  width: 320px;
  max-width: calc(100vw - 2rem);
  min-width: 280px;
}

@media (max-width: 400px) {
  .toast-notification {
    width: auto;
    min-width: auto;
    max-width: none;
  }
}
</style>
